import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { dealService, evidenceService, diagnosisService, hypothesisService, actionService, demandLayerService } from '@/services/dealService'
import type { Deal, Person, HealthCardResponse, HypothesisByDimensionResponse, ActionsByGradeResponse, ActionsByHypothesisResponse, DemandLayersResponse, DemandLayerUpdate, DemandLayerType } from '@/types'

// ============================================
// 商机相关 Hooks
// ============================================

/**
 * 获取商机列表
 */
export function useDeals(params?: { page?: number; pageSize?: number; stage?: string }) {
  return useQuery({
    queryKey: ['deals', params],
    queryFn: () => dealService.listDeals(params),
  })
}

/**
 * 获取单个商机
 */
export function useDeal(dealId: string | undefined) {
  return useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => dealService.getDeal(dealId!),
    enabled: !!dealId,
  })
}

/**
 * 创建商机
 */
export function useCreateDeal() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: dealService.createDeal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
    },
  })
}

/**
 * 更新商机
 */
export function useUpdateDeal() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ dealId, data }: { dealId: string; data: Partial<Deal> }) =>
      dealService.updateDeal(dealId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deal', variables.dealId] })
      queryClient.invalidateQueries({ queryKey: ['deals'] })
    },
  })
}

/**
 * 获取商机的关键人列表
 */
export function useDealPersons(dealId: string | undefined) {
  return useQuery<Person[]>({
    queryKey: ['dealPersons', dealId],
    queryFn: () => dealService.getDealPersons(dealId!),
    enabled: !!dealId,
    staleTime: 60000, // 1分钟内认为数据新鲜
  })
}

// ============================================
// 证据相关 Hooks
// ============================================

/**
 * 获取商机的证据列表
 */
export function useEvidences(dealId: string | undefined, params?: { sourceType?: string; status?: string }) {
  return useQuery({
    queryKey: ['evidences', dealId, params],
    queryFn: () => evidenceService.listEvidences(dealId!, params),
    enabled: !!dealId,
  })
}

/**
 * 创建证据
 * 
 * 注意：证据创建后仅刷新证据列表，不触发体检卡/假设树刷新
 * 只有在用户确认证据后才会触发假设诊断和体检卡更新
 */
export function useCreateEvidence() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: evidenceService.createEvidence,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evidences', variables.dealId] })
      // 移除：不在证据创建时刷新体检卡和假设树
      // queryClient.invalidateQueries({ queryKey: ['healthCard', variables.dealId] })
      // queryClient.invalidateQueries({ queryKey: ['hypotheses', variables.dealId] })
    },
  })
}

/**
 * 上传证据文件
 * 
 * 注意：证据创建后仅刷新证据列表，不触发体检卡/假设树刷新
 * 只有在用户确认证据后才会触发假设诊断和体检卡更新
 */
export function useUploadEvidence() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: evidenceService.uploadEvidence,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evidences', variables.dealId] })
      // 移除：不在证据创建时刷新体检卡
      // queryClient.invalidateQueries({ queryKey: ['healthCard', variables.dealId] })
    },
  })
}

/**
 * 确认证据
 * 
 * 确认证据后会触发假设诊断，因此需要刷新体检卡和假设树
 */
export function useConfirmEvidence() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: evidenceService.confirmEvidence,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evidences'] })
      // 确认证据后触发假设诊断，需要刷新体检卡和假设树
      queryClient.invalidateQueries({ queryKey: ['healthCard'] })
      queryClient.invalidateQueries({ queryKey: ['hypotheses'] })
    },
  })
}

/**
 * 纠正证据
 */
export function useCorrectEvidence() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ evidenceId, correction }: { evidenceId: string; correction: Record<string, unknown> }) =>
      evidenceService.correctEvidence(evidenceId, correction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evidences'] })
    },
  })
}

// ============================================
// 诊断相关 Hooks
// ============================================

/**
 * 获取体检卡
 * 
 * 采用事件驱动刷新策略：
 * - 不使用定时轮询（避免无效请求）
 * - 在证据确认、假设状态变更、阶段覆盖等操作后自动刷新
 * - 30秒内认为数据新鲜，避免重复请求
 */
export function useHealthCard(dealId: string | undefined) {
  return useQuery<HealthCardResponse>({
    queryKey: ['healthCard', dealId],
    queryFn: () => diagnosisService.getHealthCard(dealId!),
    enabled: !!dealId,
    staleTime: 30000, // 30秒内认为数据新鲜
    // 移除 refetchInterval，改用事件驱动刷新
  })
}

/**
 * 获取全景诊断
 */
export function useFullDiagnosis(dealId: string | undefined) {
  return useQuery({
    queryKey: ['fullDiagnosis', dealId],
    queryFn: () => diagnosisService.getFullDiagnosis(dealId!),
    enabled: !!dealId,
  })
}

/**
 * 刷新诊断
 */
export function useRefreshDiagnosis() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: diagnosisService.refreshDiagnosis,
    onSuccess: (_, dealId) => {
      queryClient.invalidateQueries({ queryKey: ['healthCard', dealId] })
      queryClient.invalidateQueries({ queryKey: ['fullDiagnosis', dealId] })
      queryClient.invalidateQueries({ queryKey: ['hypotheses', dealId] })
    },
  })
}

// ============================================
// 假设相关 Hooks
// ============================================

/**
 * 获取按维度分组的假设
 */
export function useHypotheses(dealId: string | undefined) {
  return useQuery<HypothesisByDimensionResponse>({
    queryKey: ['hypotheses', dealId],
    queryFn: () => hypothesisService.getHypothesesByDeal(dealId!),
    enabled: !!dealId,
  })
}

/**
 * 初始化假设树
 */
export function useInitializeHypotheses() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: hypothesisService.initializeHypotheses,
    onSuccess: (_, dealId) => {
      queryClient.invalidateQueries({ queryKey: ['hypotheses', dealId] })
    },
  })
}

/**
 * 确认假设
 */
export function useConfirmHypothesis() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ hypothesisId, reasoning }: { hypothesisId: string; reasoning?: string }) =>
      hypothesisService.confirmHypothesis(hypothesisId, reasoning),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hypotheses'] })
      queryClient.invalidateQueries({ queryKey: ['healthCard'] })
    },
  })
}

/**
 * 证伪假设
 */
export function useFalsifyHypothesis() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ hypothesisId, reasoning }: { hypothesisId: string; reasoning?: string }) =>
      hypothesisService.falsifyHypothesis(hypothesisId, reasoning),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hypotheses'] })
      queryClient.invalidateQueries({ queryKey: ['healthCard'] })
    },
  })
}

// ============================================
// 动作相关 Hooks
// ============================================

/**
 * 获取分级动作建议
 * 
 * @param diagnosisType - 可选，不传则返回该 Deal 下所有假设的动作
 */
export function useActionsByGrade(
  dealId: string | undefined,
  diagnosisType?: string,
  urgencyDays?: number
) {
  return useQuery<ActionsByGradeResponse>({
    queryKey: ['actions', dealId, diagnosisType, urgencyDays],
    queryFn: () => actionService.getActionsByGrade(dealId!, diagnosisType, urgencyDays),
    enabled: !!dealId,
  })
}

/**
 * 获取按假设（风险项）分组的动作
 * 
 * 用于"建议动作"页面，按风险项展示 A/B/C 分级动作
 */
export function useActionsByHypothesis(dealId: string | undefined) {
  return useQuery<ActionsByHypothesisResponse>({
    queryKey: ['actionsByHypothesis', dealId],
    queryFn: () => actionService.getActionsByHypothesis(dealId!),
    enabled: !!dealId,
  })
}

/**
 * 采纳动作
 * 
 * 采纳后动作会加入待办事项列表
 */
export function useAcceptAction() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: actionService.acceptAction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] })
      queryClient.invalidateQueries({ queryKey: ['actionsByHypothesis'] })
      queryClient.invalidateQueries({ queryKey: ['todoList'] }) // 刷新待办事项列表
    },
  })
}

/**
 * 完成动作响应类型
 */
interface CompleteActionResponse {
  actionId: string
  oldStatus: string
  newStatus: string
  message: string
  pendingSignals?: {
    id: string
    strength: string
    description: string
    requiresConfirmation: boolean
  }[]
  requiresSignalConfirmation?: boolean
}

/**
 * 完成动作
 * 
 * 返回值包含待确认信号列表，用于弹窗闭环
 */
export function useCompleteAction() {
  const queryClient = useQueryClient()
  
  return useMutation<CompleteActionResponse, Error, string>({
    mutationFn: async (actionId: string) => {
      const result = await actionService.completeAction(actionId)
      return result as CompleteActionResponse
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] })
      queryClient.invalidateQueries({ queryKey: ['actionsByHypothesis'] })
      queryClient.invalidateQueries({ queryKey: ['todoList'] })
    },
  })
}

/**
 * 拒绝动作
 */
export function useRejectAction() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (actionId: string) =>
      actionService.rejectAction(actionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] })
      queryClient.invalidateQueries({ queryKey: ['actionsByHypothesis'] })
    },
  })
}

/**
 * 标记动作为不适用
 */
export function useMarkNotApplicable() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (actionId: string) =>
      actionService.markActionNotApplicable(actionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] })
      queryClient.invalidateQueries({ queryKey: ['actionsByHypothesis'] })
    },
  })
}

// ============================================
// 信号相关 Hooks
// ============================================

/**
 * 信号响应类型
 */
interface DealSignalsResponse {
  dealId: string
  signals: {
    id: string
    actionId: string
    actionContent?: string         // 关联动作内容
    hypothesisId?: string          // 关联假设ID
    hypothesisCategory?: string    // 假设类别代码
    hypothesisName?: string        // 假设类别中文名称
    strength: string
    description: string
    observed: boolean
    observedAt?: string
    observedEvidenceId?: string
    observedNotes?: string
  }[]
  total: number
  observedCount: number
  progress: number
}

/**
 * 获取商机级别的信号列表
 */
export function useDealSignals(dealId: string | undefined, options?: {
  includeObserved?: boolean
  strengthFilter?: 'Strong' | 'Medium' | 'Weak'
}) {
  return useQuery<DealSignalsResponse>({
    queryKey: ['dealSignals', dealId, options],
    queryFn: () => actionService.getDealSignals(dealId!, options),
    enabled: !!dealId,
    staleTime: 30000, // 30秒内不重新获取
  })
}

/**
 * 信号观测响应类型
 */
interface ObserveSignalResponse {
  signalId: string
  observed: boolean
  observedAt: string
  message: string
  hypothesisUpdated?: boolean
  hypothesisUpdate?: {
    hypothesisId: string
    oldStatus: string
    newStatus: string
    isFatalGap?: boolean
  }
  reason?: string
}

/**
 * 标记信号已观测
 * 
 * 信号观测可能触发假设状态变更（强信号→确认假设），
 * 进而影响健康卡评分，因此需要刷新健康卡
 * 
 * 返回值包含假设更新信息，用于弹窗展示
 */
export function useObserveSignal() {
  const queryClient = useQueryClient()
  
  return useMutation<ObserveSignalResponse, Error, { signalId: string; evidenceId?: string; notes?: string }>({
    mutationFn: async ({ signalId, evidenceId, notes }) => {
      const result = await actionService.observeSignal(signalId, { evidenceId, notes })
      return result as ObserveSignalResponse
    },
    onSuccess: () => {
      // 刷新信号、假设和健康卡数据
      queryClient.invalidateQueries({ queryKey: ['dealSignals'] })
      queryClient.invalidateQueries({ queryKey: ['hypotheses'] })
      queryClient.invalidateQueries({ queryKey: ['healthCard'] }) // 信号观测可能影响评分
      queryClient.invalidateQueries({ queryKey: ['actionsByHypothesis'] }) // TASK-H8: 刷新动作列表中的信号状态
    },
  })
}

/**
 * 取消信号观测标记
 * 
 * 注意：取消观测不会回滚假设状态（需走人工复核流程）
 */
export function useUnobserveSignal() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ signalId }: { signalId: string }) => 
      actionService.unobserveSignal(signalId),
    onSuccess: () => {
      // 刷新信号数据（假设状态不会回滚，所以不刷新假设）
      queryClient.invalidateQueries({ queryKey: ['dealSignals'] })
      queryClient.invalidateQueries({ queryKey: ['actionsByHypothesis'] }) // TASK-H8: 刷新动作列表中的信号状态
    },
  })
}

// ============================================
// 待办事项相关 Hooks
// ============================================

/**
 * 待办事项响应类型
 */
interface TodoListResponse {
  dealId: string
  todos: {
    action: {
      id: string
      content: string
      actionGrade: 'A' | 'B' | 'C'
      status: string
      scheduledDate?: string
      timeCostDisplay?: string
      hypothesisTitle?: string
    }
    hypothesisTitle?: string
  }[]
  total: number
}

/**
 * 获取商机的待办事项列表（已采纳的动作）
 */
export function useTodoList(dealId: string | undefined) {
  return useQuery<TodoListResponse>({
    queryKey: ['todoList', dealId],
    queryFn: () => actionService.getTodoList(dealId!),
    enabled: !!dealId,
    staleTime: 30000, // 30秒内不重新获取
  })
}

/**
 * 更新动作的计划执行日期
 */
export function useUpdateScheduledDate() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ actionId, scheduledDate }: { 
      actionId: string
      scheduledDate: string | undefined 
    }) => actionService.updateScheduledDate(actionId, scheduledDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todoList'] })
      queryClient.invalidateQueries({ queryKey: ['actions'] })
      queryClient.invalidateQueries({ queryKey: ['actionsByHypothesis'] })
    },
  })
}

/**
 * 采纳动作并设置执行日期
 */
export function useAcceptActionWithDate() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ actionId, scheduledDate }: { 
      actionId: string
      scheduledDate?: string 
    }) => actionService.acceptActionWithDate(actionId, scheduledDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todoList'] })
      queryClient.invalidateQueries({ queryKey: ['actions'] })
      queryClient.invalidateQueries({ queryKey: ['actionsByHypothesis'] })
    },
  })
}

// ============================================
// 三层需求 Hooks（T1模板）
// ============================================

/**
 * 获取商机的三层需求
 */
export function useDemandLayers(dealId: string | undefined) {
  return useQuery<DemandLayersResponse>({
    queryKey: ['demandLayers', dealId],
    queryFn: () => demandLayerService.getDealDemandLayers(dealId!),
    enabled: !!dealId,
    staleTime: 60000, // 1分钟内认为数据新鲜
  })
}

/**
 * 更新某一层的需求
 */
export function useUpdateDemandLayer() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ dealId, layer, data }: { 
      dealId: string
      layer: DemandLayerType
      data: DemandLayerUpdate 
    }) => demandLayerService.updateDemandLayer(dealId, layer, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['demandLayers', variables.dealId] })
    },
  })
}

